import React from 'react'
import cn from 'classnames'
import { SATOSHIS, toBitcoin, toSats } from 'lib/bsv-util'
import { useBsvExchangeRate, usdToSats } from 'api/useBsvExchangeRate'
import SatsIcon from 'components/shared/SatsIcon'
import styles from './editable-amount.module.scss'

interface EditableAmountProps {
    value: number | null
    isBlockInput?: boolean
    useSats?: boolean
    useUsd?: boolean
    onChange: (value: number | null) => void
    onUsdValueChange?: (value: number | null) => void
    defaultValue?: number
    contextStyles?: string
    label?: string
    subValue?: string
    inlineAction?: React.ReactNode
}

export default function EditableAmount({
    value,
    isBlockInput = false,
    useSats = false,
    useUsd = false,
    onChange,
    onUsdValueChange,
    defaultValue = 144,
    contextStyles,
    label,
    subValue,
    inlineAction,
}: EditableAmountProps) {
    const exchangeRate = useBsvExchangeRate({ enabled: useUsd })
    const editAmountClass = useSats
        ? styles.satsEditAmount
        : useUsd
        ? styles.usdEditAmount
        : styles.bitcoinEditAmount
    const isDecimalInput = !isBlockInput && !useSats
    const maxDecimalPlaces = useUsd ? 4 : 8
    const formatUsdInput = React.useCallback(
        (sats: number) => {
            if (!exchangeRate) return ''

            const dollars = (sats / SATOSHIS) * exchangeRate
            if (!Number.isFinite(dollars)) return ''

            const formatted = dollars.toFixed(maxDecimalPlaces).replace(/\.?0+$/, '')
            return formatted || '0'
        },
        [exchangeRate, maxDecimalPlaces],
    )
    const [displayValue, setDisplayValue] = React.useState<string>(() => {
        if (isDecimalInput && typeof value === 'number') {
            return useUsd ? formatUsdInput(value) : toBitcoin(value)
        }
        return ''
    })
    const [isEditing, setIsEditing] = React.useState(false)

    React.useEffect(() => {
        if (!isDecimalInput || isEditing) return
        if (typeof value === 'number') {
            setDisplayValue(useUsd ? formatUsdInput(value) : toBitcoin(value))
        } else {
            setDisplayValue('')
        }
    }, [value, isDecimalInput, isEditing, useUsd, formatUsdInput])

    return (
        <div className={cn(styles.container, isBlockInput && styles.blockContainer)}>
            {label && <div className={styles.inputLabel}>{label}</div>}
            <div
                className={cn(
                    styles.editableAmountComp,
                    editAmountClass,
                    inlineAction && styles.withInlineAction,
                    contextStyles,
                )}
            >
                <span
                    className={cn(
                        styles.currencySymbol,
                        !useSats && !useUsd && styles.bitcoinSymbol,
                        useUsd && styles.usdSymbol,
                    )}
                >
                    {isBlockInput ? (
                        <i className="fa fa-cube" />
                    ) : useSats ? (
                        <SatsIcon showBars={true} contextStyles={cn(styles.satsIcon)} />
                    ) : useUsd ? (
                        '$'
                    ) : (
                        <i className="fa fa-btc" />
                    )}
                </span>

                <div className={styles.amountInputRow}>
                    <input
                        className={styles.amountInput}
                        type={isDecimalInput ? 'text' : 'number'}
                        inputMode={isDecimalInput ? 'decimal' : undefined}
                        pattern={
                            isDecimalInput ? `[0-9]*\\.?[0-9]{0,${maxDecimalPlaces}}` : undefined
                        }
                        value={
                            isDecimalInput
                                ? displayValue
                                : typeof value === 'number'
                                ? isBlockInput
                                    ? value
                                    : useSats
                                    ? value
                                    : toBitcoin(value)
                                : ''
                        }
                        onChange={ev => {
                            const val = ev.target.value

                            if (isDecimalInput) {
                                if (val === '') {
                                    setDisplayValue('')
                                    onChange(null)
                                    onUsdValueChange?.(null)
                                    return
                                }

                                if (val === '.') {
                                    setDisplayValue('0.')
                                    onChange(null)
                                    onUsdValueChange?.(null)
                                    return
                                }

                                const decimalInputPattern = new RegExp(
                                    `^\\d*(\\.\\d{0,${maxDecimalPlaces}})?$`,
                                )
                                if (!decimalInputPattern.test(val)) {
                                    return
                                }

                                setDisplayValue(val)

                                const amount = Number(val)
                                if (isNaN(amount)) {
                                    onChange(null)
                                    onUsdValueChange?.(null)
                                } else if (useUsd) {
                                    onUsdValueChange?.(amount)
                                    if (!exchangeRate) onChange(null)
                                    else onChange(usdToSats(amount, exchangeRate))
                                } else {
                                    onUsdValueChange?.(null)
                                    onChange(toSats(amount))
                                }
                                return
                            }

                            if (val === '') {
                                onChange(isBlockInput ? defaultValue : null)
                            } else if (isBlockInput) {
                                const num = Number(val)
                                if (isNaN(num) || num < 1) onChange(defaultValue)
                                else onChange(num)
                            } else if (useSats) {
                                const numSats = Number(val)
                                if (isNaN(numSats)) onChange(null)
                                else onChange(numSats)
                            } else {
                                const numBitcoin = Number(val)
                                if (isNaN(numBitcoin)) onChange(null)
                                else onChange(toSats(numBitcoin))
                            }
                        }}
                        onFocus={() => {
                            if (isDecimalInput) setIsEditing(true)
                        }}
                        onBlur={() => {
                            if (!isDecimalInput) return
                            setIsEditing(false)
                            if (typeof value === 'number') {
                                setDisplayValue(useUsd ? formatUsdInput(value) : toBitcoin(value))
                            } else {
                                setDisplayValue('')
                            }
                        }}
                        placeholder={
                            isBlockInput
                                ? defaultValue.toString()
                                : useSats
                                ? '0'
                                : useUsd
                                ? '0.000'
                                : '0.0'
                        }
                    />
                    {subValue && <div className={styles.subValueInline}>{subValue}</div>}
                </div>
                {inlineAction && <div className={styles.inlineAction}>{inlineAction}</div>}
            </div>
        </div>
    )
}
